import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { GungModalService } from '../gung-modal/gung-modal.service';
import { OrderServicesService, OrderServicesTicket } from '../order-services/order-services.service';
import type { WarrantySelectionAction } from '../../components/return-warranty/return-warranty-create-view-modal/return-warranty-create-view-modal.component';

export interface ReturnWarrantyActionsContext {
  getTicket: () => OrderServicesTicket;
  isAdmin: boolean;
  isSales: boolean;
  onStatusChanged: (ticket: OrderServicesTicket) => void;
  onDeleted: () => void;
  onEditHeader: () => void;
  onSendToErp: () => Observable<any>;
}

@Injectable({
  providedIn: 'root'
})
export class ReturnWarrantySelectionActionConfigService {
  protected translationService = inject(TranslateService);
  protected orderServicesService = inject(OrderServicesService);
  protected gungModalService = inject(GungModalService);

  getStatusActions(
    claimStatusMetadata: any[],
    context: ReturnWarrantyActionsContext
  ): WarrantySelectionAction<string>[] {
    return (claimStatusMetadata || []).map(status => ({
      label: this.translationService.instant('STATUS') + ' ' + status.name,
      performAction: () => {
        context.getTicket().status = status.name;
        this.orderServicesService
          .postOrderServices(context.getTicket())
          .subscribe(ticket => context.onStatusChanged(ticket));
        return of();
      }
    }));
  }

  getSelectionActions(
    context: ReturnWarrantyActionsContext,
    actions: WarrantySelectionAction<string>[] = []
  ): Observable<WarrantySelectionAction<string>[]> {
    actions.push(this.getDeleteAction(context));

    if (context.isAdmin || context.isSales) {
      actions.push(this.getEditHeaderAction(context));
    }

    if ((context.isAdmin || context.isSales) && !context.getTicket()?.orderId) {
      actions.push(this.getCreateReturnOrderAction(context));
    }

    return of(actions);
  }

  protected getDeleteAction(context: ReturnWarrantyActionsContext): WarrantySelectionAction<string> {
    return {
      label: 'DELETE',
      performAction: () => {
        this.gungModalService
          .openConfirmYesNoModal(undefined, this.translationService.instant('REMOVE') + ` ${context.getTicket().id}?`, {
            size: 'sm'
          })
          .then(result => {
            if (result) {
              this.orderServicesService.deleteOrderServices(context.getTicket().id).subscribe();
              context.onDeleted();
            }
          });
        return of();
      }
    };
  }

  protected getEditHeaderAction(context: ReturnWarrantyActionsContext): WarrantySelectionAction<string> {
    return {
      label: 'EDIT_HEADER',
      performAction: () => {
        context.onEditHeader();
        return of();
      }
    };
  }

  protected getCreateReturnOrderAction(context: ReturnWarrantyActionsContext): WarrantySelectionAction<string> {
    return {
      label: 'CREATE_RETURN_ORDER_IN_ERP',
      performAction: () => context.onSendToErp()
    };
  }
}
